import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { login as loginApi } from '../api/auth';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, Home } from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();

  // Supabase e-posta doğrulama sonrası /login'e döndüğümüzde bilgi tostu göster
  useEffect(() => {
    if (window.location.hash && window.location.hash.includes('type=signup')) {
      // Her ihtimale karşı session'ı kapat
      supabase.auth.signOut().catch(() => {});
      toast.success('Hesabınız etkinleştirildi, giriş yapabilirsiniz');
      // Hash'i temizle
      window.history.replaceState(null, '', '/login');
    }
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Worker proxy üzerinden login
      const r = await loginApi(data.email, data.password);
      const tokens = await r.json();
      if (r.ok && tokens?.access_token) {
        try {
          await supabase.auth.setSession({
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token || '',
          });
        } catch (_) {}
        toast.success('Giriş başarılı!');
        navigate('/dashboard');
      } else {
        const msg = (tokens?.error_description || tokens?.error?.message || '').toLowerCase();
        if (msg.includes('invalid')) {
          toast.error('E-posta veya şifre hatalı.');
        } else {
          toast.error('Giriş başarısız! E-posta veya şifre yanlış.');
        }
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">

          {/* Glassmorphism Card */}
          <div className="backdrop-blur-xl bg-white/10 rounded-3xl p-8 shadow-2xl border border-white/20 relative">
            {/* Home Icon - Sağ Üst */}
            <Link
              to="/"
              className="absolute top-6 right-6 group p-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl hover:bg-white/30 hover:shadow-lg transform hover:scale-110 transition-all duration-300"
            >
              <Home className="h-5 w-5 text-white group-hover:text-white/90" />
            </Link>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Hoş Geldiniz
              </h2>
              <p className="text-white/70">
                Hesabınıza giriş yaparak devam edin
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                  E-posta adresi
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'E-posta adresi zorunludur',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Geçerli bir e-posta adresi giriniz'
                      }
                    })}
                    type="email"
                    className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                    placeholder="ornek@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-300">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                  Şifre
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                  </div>
                  <input
                    {...register('password', {
                      required: 'Şifre zorunludur',
                      minLength: {
                        value: 8,
                        message: 'Şifre en az 8 karakter olmalıdır'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/60 hover:text-white transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-300">{errors.password.message}</p>
                )}
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end -mb-2">
                <Link to="/forgot-password" className="text-white/90 hover:text-white text-sm font-medium">
                  Şifremi unuttum
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 py-4 px-6 rounded-2xl text-white font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span className="relative z-10 flex items-center justify-center">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Giriş yapılıyor...
                    </>
                  ) : (
                    <>
                      Giriş Yap
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-center text-white/70">
                Hesabınız yok mu?{' '}
                <Link
                  to="/register"
                  className="text-white font-semibold hover:text-white/80 transition-colors"
                >
                  Kayıt olun
                </Link>
              </p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-center">
            <p className="text-white/60 text-sm">
              Güvenli ve hızlı giriş sistemi
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;